import { TooltipButton } from "@/components/atoms/TooltipButton";
import { questionsSortConfig } from "@/hooks/useSortBy/sortConfigs";
import { useSortByWithConfig } from "@/hooks/useSortBy/useSortByWithConfig";
import { Eye, GalleryVerticalEnd, List, ListTree } from "lucide-react";
import { useState } from "react";
import { QuestionsListProps, SurveyQuestionsOrSurveyBankQuestions } from ".";
import { QuestionsListRow } from "../QuestionsListRow";

export function QuestionsList({ 
    questions, 
    actions = false, 
    RowRenderer,
    children 
}: Readonly<QuestionsListProps>) {
    const [showArchived, setShowArchived] = useState(false);
    const [groupQuestions, setGroupQuestions] = useState(false);
    
    const {
        Icon: QuestionsSortByIcon,
        label: questionsSortByLabel,
        nextOption: questionsSortByNextOption,
        sortedResult: sortedQuestions,
    } = useSortByWithConfig(
        questionsSortConfig,
        questions ?? [],
        'created_at_desc'
    );
    
    // Determine which actions should be visible
    const showSortBy = typeof actions === 'boolean' ? actions : actions?.sortBy ?? false;
    const showShowArchive = typeof actions === 'boolean' ? actions : actions?.showArchive ?? false;
    const showGroupQuestions = typeof actions === 'boolean' ? actions : actions?.groupQuestions ?? false;
    
    const showArchiveHandler = () => {
        setShowArchived(prev => !prev);
    };
    
    const groupQuestionsHandler = () => {
        setGroupQuestions(prev => !prev);
    };
    
    if (children) {
        return (
            <>
                {children({
                    SortByButton: ({ className }) => (
                        <TooltipButton
                            tooltipContent={`Sort by ${questionsSortByLabel}`}
                            onClick={questionsSortByNextOption}
                            className={className}
                            size="action"
                            variant="secondary"
                        >
                            {QuestionsSortByIcon}
                        </TooltipButton>
                    ),
                    ShowArchiveButton: ({ className }) => (
                        <TooltipButton
                            tooltipContent={showArchived ? "Show Active Questions" : "Show Archived Questions"}
                            onClick={showArchiveHandler}
                            className={className}
                            size="action"
                            variant="secondary"
                        >
                            {showArchived ? <GalleryVerticalEnd size={16} /> : <Eye size={16} />}
                        </TooltipButton>
                    ),
                    GroupQuestionsButton: ({ className }) => (
                        <TooltipButton
                            tooltipContent={groupQuestions ? "Ungroup questions" : "Group questions by dependencies"}
                            onClick={groupQuestionsHandler}
                            className={className}
                            size="action"
                            variant="secondary"
                        >
                            {groupQuestions ? <ListTree size={16} /> : <List size={16} />}
                        </TooltipButton>
                    ),
                    QuestionItem: ({ children: renderItem }) => (
                        <QuestionsListFACCItem questions={sortedQuestions}>
                            {renderItem}
                        </QuestionsListFACCItem>
                    ),
                })}
            </>
        );
    }
    const ComponentToRender = RowRenderer || QuestionsListRow;
    
    return (
        <div>
            {(showSortBy || showShowArchive || showGroupQuestions) && (
                <div className="flex gap-2 mb-4">
                    {showSortBy && (
                        <TooltipButton
                            tooltipContent={`Sort by ${questionsSortByLabel}`}
                            onClick={questionsSortByNextOption}
                            size="action"
                            variant="secondary"
                        >
                            {QuestionsSortByIcon}
                        </TooltipButton>
                    )}
                    {showShowArchive && (
                        <TooltipButton
                            tooltipContent={showArchived ? "Show Active Questions" : "Show Archived Questions"}
                            onClick={showArchiveHandler}
                            size="action"
                            variant="secondary"
                        >
                            {showArchived ? <GalleryVerticalEnd size={16} /> : <Eye size={16} />}
                        </TooltipButton>
                    )}
                    {showGroupQuestions && (
                        <TooltipButton
                            tooltipContent={groupQuestions ? "Ungroup questions" : "Group questions by dependencies"}
                            onClick={groupQuestionsHandler}
                            size="action"
                            variant="secondary"
                        >
                            {groupQuestions ? <ListTree size={16} /> : <List size={16} />}
                        </TooltipButton>
                    )}
                </div>
            )}
            <ul className="space-y-2">
                {sortedQuestions.map((question) => (
                    <ComponentToRender key={question.id} question={question} />
                ))}
            </ul>
        </div>
    );
}

function QuestionsListFACCItem({
    questions,
    children,
}: {
    questions: SurveyQuestionsOrSurveyBankQuestions[];
    children: (question: SurveyQuestionsOrSurveyBankQuestions) => React.ReactNode;
}) {
    return (
        <ul>
            {questions.map((question) => (
                <li key={question.id}>{children(question)}</li>
            ))}
        </ul>
    );
}